import React, { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { imageurl } from "../../App";
import { getProductImageUrl, handleProductImageError, applyStoreTitle } from "../../util/storeLogoHelper";
import {
  FaSearch,
  FaTruck,
  FaCheckCircle,
  FaClock,
  FaMapMarkerAlt,
  FaShoppingBag,
  FaTimesCircle
} from "react-icons/fa";

export default function TrackOrder() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initialQuery = searchParams.get("tracking") || "";

  const [query, setQuery] = useState(initialQuery);
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const handleSearch = useCallback((trackingId) => {
    const q = trackingId || query;
    if (!q || !q.trim()) return;

    setLoading(true);
    setErrorMsg("");
    setOrder(null);

    axios
      .post(`/api/track-order`, { tracking_query: q.trim() })
      .then((res) => {
        setLoading(false);
        if (res.data.status === 200) {
          setOrder(res.data.order);
        } else {
          setErrorMsg(res.data.message || "No order found matching this tracking reference.");
        }
      })
      .catch(() => {
        setLoading(false);
        setErrorMsg("Failed to connect to tracking service. Please try again.");
      });
  }, [query]);

  useEffect(() => {
    window.scrollTo(0, 0);
    applyStoreTitle(null, "Track Your Order");
    if (initialQuery) {
      handleSearch(initialQuery);
    }
  }, [initialQuery, handleSearch]);

  const onFormSubmit = (e) => {
    e.preventDefault();
    handleSearch(query);
  };

  const currentStep = order?.status_step || 1;

  return (
    <div>
      {/* Header Banner */}
      <div className="page-header-modern">
        <div className="container text-center">
          <ul className="breadcrumb-modern justify-content-center">
            <li>
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-separator">/</li>
            <li className="active">Track Order</li>
          </ul>
          <h1 className="page-header-title">Track Shipment</h1>
          <p className="text-muted mx-auto" style={{ maxWidth: "500px", fontSize: "14px" }}>
            Enter the tracking number from your order confirmation to view delivery progress.
          </p>
        </div>
      </div>

      <div className="container py-5" style={{ maxWidth: "760px" }}>
        {/* Tracking Search Input Card */}
        <div className="bg-white p-4 rounded-4 border mb-5" style={{ boxShadow: "var(--shadow-sm)" }}>
          <form onSubmit={onFormSubmit} className="d-flex gap-2">
            <div className="position-relative flex-grow-1">
              <input
                type="text"
                className="form-control py-3 ps-4 pe-3"
                placeholder="Tracking number, for example PK-8F92A1"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                style={{ fontSize: "14.5px", borderRadius: "10px" }}
                required
              />
            </div>
            <button
              type="submit"
              className="btn btn-dark px-4 py-3 fw-bold rounded-3 d-flex align-items-center gap-2"
              disabled={loading}
              style={{ fontSize: "14.5px" }}
            >
              <FaSearch size={14} />
              {loading ? "Searching..." : "Track"}
            </button>
          </form>
        </div>

        {/* Error Message */}
        {errorMsg && (
          <div className="p-4 bg-white border border-danger-subtle rounded-4 text-center mb-4">
            <FaTimesCircle size={32} className="text-danger mb-2" />
            <h5 className="fw-bold text-danger mb-1">Order Not Found</h5>
            <p className="text-muted mb-0" style={{ fontSize: "13.5px" }}>{errorMsg}</p>
          </div>
        )}

        {/* Order Details & Visual Tracker */}
        {order && (
          <div className="bg-white p-4 p-md-5 rounded-4 border" style={{ boxShadow: "var(--shadow-sm)" }}>
            {/* Header info */}
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 pb-3 mb-4 border-bottom">
              <div>
                <span className="badge bg-dark mb-1">Tracking: {order.tracking_no || `ORD-${order.id}`}</span>
                <h4 className="fw-bold mb-0">Order #{order.id}</h4>
                <span className="text-muted" style={{ fontSize: "12.5px" }}>
                  Placed on {order.created_at ? new Date(order.created_at).toLocaleDateString() : "Recent"}
                </span>
              </div>
              <div className="text-end">
                <span className="badge bg-success px-3 py-2" style={{ fontSize: "13px" }}>
                  {order.status_label || "In Progress"}
                </span>
              </div>
            </div>

            {/* Visual Timeline Progress Bar */}
            <div className="my-4 py-2">
              <h6 className="text-muted fw-bold mb-4" style={{ fontSize: "12px", letterSpacing: "0.5px" }}>
                SHIPMENT TIMELINE
              </h6>
              <div className="d-flex align-items-center justify-content-between position-relative">
                {/* Step 1: Placed */}
                <div className="text-center" style={{ zIndex: 2 }}>
                  <div
                    className={`rounded-circle mx-auto mb-1 d-flex align-items-center justify-content-center text-white ${
                      currentStep >= 1 ? "bg-success" : "bg-light text-muted"
                    }`}
                    style={{ width: "36px", height: "36px", fontSize: "14px" }}
                  >
                    <FaCheckCircle />
                  </div>
                  <span className="fw-bold d-block" style={{ fontSize: "12px" }}>Order Placed</span>
                </div>

                {/* Step 2: Confirmed */}
                <div className="text-center" style={{ zIndex: 2 }}>
                  <div
                    className={`rounded-circle mx-auto mb-1 d-flex align-items-center justify-content-center text-white ${
                      currentStep >= 2 ? "bg-success" : "bg-light text-muted"
                    }`}
                    style={{ width: "36px", height: "36px", fontSize: "14px" }}
                  >
                    <FaCheckCircle />
                  </div>
                  <span className="fw-bold d-block" style={{ fontSize: "12px" }}>Confirmed</span>
                </div>

                {/* Step 3: Processing */}
                <div className="text-center" style={{ zIndex: 2 }}>
                  <div
                    className={`rounded-circle mx-auto mb-1 d-flex align-items-center justify-content-center text-white ${
                      currentStep >= 3 ? "bg-primary" : "bg-light text-muted"
                    }`}
                    style={{ width: "36px", height: "36px", fontSize: "14px" }}
                  >
                    <FaClock />
                  </div>
                  <span className="fw-bold d-block" style={{ fontSize: "12px" }}>Processing</span>
                </div>

                {/* Step 4: Shipped */}
                <div className="text-center" style={{ zIndex: 2 }}>
                  <div
                    className={`rounded-circle mx-auto mb-1 d-flex align-items-center justify-content-center text-white ${
                      currentStep >= 4 ? "bg-primary" : "bg-light text-muted"
                    }`}
                    style={{ width: "36px", height: "36px", fontSize: "14px" }}
                  >
                    <FaTruck />
                  </div>
                  <span className="fw-bold d-block" style={{ fontSize: "12px" }}>Shipped</span>
                </div>

                {/* Step 5: Delivered */}
                <div className="text-center" style={{ zIndex: 2 }}>
                  <div
                    className={`rounded-circle mx-auto mb-1 d-flex align-items-center justify-content-center text-white ${
                      currentStep >= 5 ? "bg-success" : "bg-light text-muted"
                    }`}
                    style={{ width: "36px", height: "36px", fontSize: "14px" }}
                  >
                    <FaCheckCircle />
                  </div>
                  <span className="fw-bold d-block" style={{ fontSize: "12px" }}>Delivered</span>
                </div>
              </div>
            </div>

            {/* Delivery Destination */}
            <div className="p-3 bg-light rounded-3 my-4">
              <span className="text-muted fw-semibold" style={{ fontSize: "12px" }}>DELIVERY DESTINATION</span>
              <p className="fw-bold mb-0" style={{ fontSize: "14px" }}>
                {order.firstname} {order.lastname}
              </p>
              <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
                <FaMapMarkerAlt className="me-1 text-danger" /> {order.address}, {order.city} {order.state}
              </p>
            </div>

            {/* Ordered Items & Pricing Breakdown */}
            {order.orderitems && order.orderitems.length > 0 && (
              <div>
                <h6 className="fw-bold mb-3" style={{ fontSize: "14px" }}>
                  Items in this Shipment ({order.orderitems.length})
                </h6>
                <div className="d-flex flex-column gap-2 mb-4">
                  {order.orderitems.map((item, idx) => {
                    const prod = item.products;
                    const itemImg = prod?.product_image?.[0]?.image || prod?.image;
                    const paidUnit = Number(item.unit_price ?? item.price ?? 0);
                    const origUnit = Number(item.original_price ?? item.productsprice?.seeling_price ?? paidUnit);
                    const hasDisc = origUnit > paidUnit;
                    const lineTotal = Number(item.line_total ?? (paidUnit * (item.qty || 1)));
                    const origLineTotal = origUnit * (item.qty || 1);

                    return (
                      <div key={idx} className="d-flex align-items-center justify-content-between gap-3 p-3 border rounded-3 bg-white">
                        <div className="d-flex align-items-center gap-3">
                          <div
                            style={{
                              width: "52px",
                              height: "52px",
                              borderRadius: "8px",
                              overflow: "hidden",
                              border: "1px solid #e2e8f0",
                              flexShrink: 0,
                            }}
                          >
                            <img
                              src={getProductImageUrl(itemImg)}
                              alt={prod?.name || "Product"}
                              style={{ width: "100%", height: "100%", objectFit: "cover" }}
                              onError={handleProductImageError}
                            />
                          </div>
                          <div>
                            <h6 className="fw-bold mb-1" style={{ fontSize: "13.5px" }}>{item.display_name || prod?.name || "Product"}</h6>
                            <div className="d-flex align-items-center gap-2 flex-wrap" style={{ fontSize: "12px" }}>
                              <span className="text-muted">Qty: {item.qty} × Rs. {paidUnit.toLocaleString()}</span>
                              {hasDisc && (
                                <span className="text-muted text-decoration-line-through">
                                  Rs. {origUnit.toLocaleString()}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="text-end">
                          <span className="fw-bold" style={{ fontSize: "14px" }}>
                            Rs. {lineTotal.toLocaleString()}
                          </span>
                          {hasDisc && (
                            <div className="text-muted text-decoration-line-through" style={{ fontSize: "12px" }}>
                              Rs. {origLineTotal.toLocaleString()}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Financial Summary Breakdown */}
                <div className="p-3 bg-light rounded-3 border">
                  <div className="d-flex flex-column gap-2" style={{ fontSize: "13.5px" }}>
                    <div className="d-flex justify-content-between text-muted">
                      <span>Items Subtotal (Real Price)</span>
                      <span className="fw-semibold text-dark">
                        Rs. {Number(order.original_amount ?? order.total_amount).toLocaleString()}
                      </span>
                    </div>

                    {Number(order.catalog_discount || (Number(order.total_discount || 0) - Number(order.coupon_discount || 0))) > 0 && (
                      <div className="d-flex justify-content-between text-success fw-semibold">
                        <span>Catalog Discount</span>
                        <span>-Rs. {Number(order.catalog_discount || (Number(order.total_discount || 0) - Number(order.coupon_discount || 0))).toLocaleString()}</span>
                      </div>
                    )}

                    {Number(order.catalog_discount || (Number(order.total_discount || 0) - Number(order.coupon_discount || 0))) > 0 && (
                      <div className="d-flex justify-content-between text-muted fw-semibold bg-white p-2 rounded border">
                        <span>Items Subtotal (Discounted)</span>
                        <span className="text-dark">
                          Rs. {Number(order.items_subtotal ?? (Number(order.original_amount) - Number(order.catalog_discount || 0))).toLocaleString()}
                        </span>
                      </div>
                    )}

                    {Number(order.coupon_discount || 0) > 0 && (
                      <div className="d-flex justify-content-between text-danger fw-semibold">
                        <span>Coupon Discount ({order.coupon_code || "COUPON"})</span>
                        <span>-Rs. {Number(order.coupon_discount).toLocaleString()}</span>
                      </div>
                    )}

                    <div className="d-flex justify-content-between text-muted">
                      <span>Nationwide Shipping</span>
                      <span className={Number(order.shipping_fee || 0) === 0 ? "fw-bold text-success" : "fw-bold text-dark"}>
                        {Number(order.shipping_fee || 0) === 0 ? "FREE" : `Rs. ${Number(order.shipping_fee).toLocaleString()}`}
                      </span>
                    </div>

                    <div className="d-flex justify-content-between text-muted">
                      <span>Payment Mode</span>
                      <span className="fw-semibold text-dark">
                        {order.payment_mode === "COD" ? "Cash on Delivery (COD)" : order.payment_mode}
                      </span>
                    </div>

                    <hr className="my-2" />

                    <div className="d-flex justify-content-between align-items-baseline">
                      <span className="fw-bold" style={{ fontSize: "15px", color: "#0f172a" }}>
                        Total Amount
                      </span>
                      <span className="fw-extrabold" style={{ fontSize: "18px", color: "var(--accent)" }}>
                        Rs. {Number(order.total_amount).toLocaleString()}
                      </span>
                    </div>

                    {Number(order.total_discount || 0) > 0 && (
                      <div className="p-2 rounded-2 bg-success-subtle d-flex align-items-center gap-2 text-success mt-1">
                        <span className="fw-bold" style={{ fontSize: "12.5px" }}>
                          🎉 Total Savings on this Order: Rs. {Number(order.total_discount).toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="text-center mt-5">
          <Link to="/collection" className="btn btn-outline-dark rounded-pill px-4 py-2 fw-semibold">
            <FaShoppingBag className="me-2" /> Back to Store
          </Link>
        </div>
      </div>
    </div>
  );
}
